import { AlertCircle, Check } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface AdjustmentConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adjustments: {
    difficulty?: 'increase' | 'decrease' | 'maintain';
    pace?: 'faster' | 'slower' | 'maintain';
    focusChange?: boolean;
  };
  applyToFuture: boolean;
  affectedWeeks: number;
  affectedTasks: number;
  onConfirm: () => void;
  onCancel: () => void;
  isApplying: boolean;
}

export function AdjustmentConfirmDialog({
  open,
  onOpenChange,
  adjustments,
  applyToFuture,
  affectedWeeks,
  affectedTasks,
  onConfirm,
  onCancel,
  isApplying,
}: AdjustmentConfirmDialogProps) {
  const getDifficultyText = (type: string) => {
    if (type === 'increase') return 'Harder questions (difficulty +1)';
    if (type === 'decrease') return 'Easier questions (difficulty -1)';
    return 'No change';
  };

  const getPaceText = (type: string) => {
    if (type === 'faster') return '+20% MCQs, -15% time per session';
    if (type === 'slower') return '-15% MCQs, +20% time per session';
    return 'No change';
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Plan Adjustments</AlertDialogTitle>
          <AlertDialogDescription>
            Review the changes that will be applied to your study plan
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {/* Changes Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">What's Changing?</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aspect</TableHead>
                    <TableHead>Adjustment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adjustments.difficulty && adjustments.difficulty !== 'maintain' && (
                    <TableRow>
                      <TableCell className="font-medium">Difficulty</TableCell>
                      <TableCell>
                        <Badge variant={adjustments.difficulty === 'increase' ? 'default' : 'secondary'}>
                          {getDifficultyText(adjustments.difficulty)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )}
                  {adjustments.pace && adjustments.pace !== 'maintain' && (
                    <TableRow>
                      <TableCell className="font-medium">Pace</TableCell>
                      <TableCell>
                        <Badge variant={adjustments.pace === 'faster' ? 'default' : 'secondary'}>
                          {getPaceText(adjustments.pace)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )}
                  {adjustments.focusChange && (
                    <TableRow>
                      <TableCell className="font-medium">Focus</TableCell>
                      <TableCell>
                        <Badge variant="outline">Topics will be re-prioritized</Badge>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Impact Preview */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Impact</AlertTitle>
            <AlertDescription>
              This will affect <strong>{affectedWeeks}</strong> week{affectedWeeks !== 1 ? 's' : ''} and{' '}
              <strong>{affectedTasks}</strong> task{affectedTasks !== 1 ? 's' : ''}.
              {applyToFuture
                ? ' All future weeks will be updated.'
                : ' Only this week will be modified.'}
            </AlertDescription>
          </Alert>
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isApplying}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isApplying}>
            {isApplying ? (
              <>Applying...</>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirm Changes
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
